import { type Dispatch, type SetStateAction, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Beacon } from '@belong/ai-pointer';
import { formatUtcDateTimeToTimeZone, useModal } from '@belong/common';
import { Text, Button } from '@belong/ui';
import { RescheduleAppointmentModal } from 'accounts/components/reschedule-appointment-modal/reschedule-appointment-modal';
import { SECTIONS_STRINGS } from 'accounts/constants/strings/sections.strings';
import { useUpcomingAppointments } from 'accounts/hooks/homeowners/use-upcoming-appointments';
import { useHomeownerInspections } from 'accounts/hooks/homeowners/use-homeowner-inspections';
import { findOnboardingInspection } from 'accounts/utils/inspections';
import type { UnitSummaryModel } from 'api/models';
import { getMinutes, differenceInHours } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { selectUpcomingAppointments } from 'store/redux/homeowner-accounts/selectors';
import { RESCHEDULE_INSPECTION_HELP } from 'strings/homeowner-accounts.help';

type Appointment = {
  appointmentStatus: string;
  startDate: string;
  endDate: string;
};

const getNextAppointment = (appointments: Appointment[] | undefined) =>
  [...(appointments ?? [])].sort(
    (a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime(),
  )[0];

type Props = {
  coverageRegionId: number;
  unit: UnitSummaryModel;
  timeZone: string;
  setPropertySectionTitleVisibilityByProperty: Dispatch<
    SetStateAction<
      Record<
        string,
        Partial<
          Record<
            | 'upcoming-appointments'
            | 'home-access-instructions'
            | 'home-improvements-tracking',
            boolean
          >
        >
      >
    >
  >;
};

export function UpcomingAppointmentsSection({
  coverageRegionId,
  timeZone,
  unit,
  setPropertySectionTitleVisibilityByProperty,
}: Props) {
  const [
    isRescheduleAppointmentModalOpen,
    openRescheduleAppointmentModal,
    closeRescheduleAppoinmentModal,
  ] = useModal();
  // `basicInfo` and its `unitId` are both optional in the generated model, but
  // this section only renders for a unit out of the account's property list,
  // where both are populated — every read below already assumed so.
  const unitId = unit.basicInfo!.unitId!;
  const { loading } = useUpcomingAppointments(unitId);
  const { isLoadingInspections, inspections } = useHomeownerInspections();
  const upcomingAppointments = useSelector(selectUpcomingAppointments);

  useEffect(() => {
    const { startDate, endDate } =
      getNextAppointment(upcomingAppointments?.[unitId]) || {};

    if (startDate && endDate) {
      setPropertySectionTitleVisibilityByProperty((prev) => ({
        ...prev,
        [unitId]: {
          ...prev[unitId],
          'upcoming-appointments': true,
        },
      }));
    }
  }, [
    upcomingAppointments,
    unitId,
    setPropertySectionTitleVisibilityByProperty,
  ]);

  const upcomingAppointment = getNextAppointment(upcomingAppointments[unitId]);

  if (loading || isLoadingInspections || !upcomingAppointment) {
    return null;
  }

  const { appointmentStatus, startDate, endDate } = upcomingAppointment;

  const currentInspection = findOnboardingInspection(inspections, unitId);

  const isHandledByAnExternalProvider =
    currentInspection?.isHandledByAnExternalProvider || false;

  if (!startDate || !endDate) {
    window.DD_RUM?.addError?.(
      new Error(`Upcoming Appointment without start/end date`),
      {
        validation_error: 'true',
      },
    );

    return null;
  }

  const canBeRescheduled =
    ['Scheduled', 'Reserved'].includes(appointmentStatus) &&
    differenceInHours(new Date(endDate), new Date()) > 24;

  const getSlotFormat = (date: any) => {
    const zonedDate = utcToZonedTime(date, timeZone);
    const zonedDateMinutes = getMinutes(zonedDate);

    return zonedDateMinutes ? 'h:mma' : 'ha';
  };

  const slot = `${formatUtcDateTimeToTimeZone({
    dateTime: startDate,
    format: getSlotFormat(startDate),
    timeZone,
  })} - ${formatUtcDateTimeToTimeZone({ dateTime: endDate, format: `${getSlotFormat(endDate)} z`, timeZone })}`;

  const handleRescheduleClick = () => openRescheduleAppointmentModal();

  const handleRescheduleAppointmentModalDismiss = () =>
    closeRescheduleAppoinmentModal();

  return (
    <>
      <Beacon
        id="reschedule-inspection"
        target='[data-pointer="reschedule-inspection"] button'
        help={RESCHEDULE_INSPECTION_HELP}
        active={canBeRescheduled}
        pinColor="#3ee4a9"
        pinCorner="top-left"
        openBelowPin
      />
      <Text fontWeight="semibold" className="text-[16px]">
        {SECTIONS_STRINGS['upcoming_appointments.title']}
      </Text>
      <div className="flex w-full flex-col sm:flex-row">
        <div className="mb-xl mt-xs flex shrink-0 flex-col items-start justify-between border-0 border-l-3 border-solid border-blue pl-xs sm:mt-sm">
          <Text>
            {SECTIONS_STRINGS['upcoming_appointments.inspection']}{' '}
            {appointmentStatus}
          </Text>
          <Text fontWeight="semibold" className="mt-xs text-body">
            {`${formatUtcDateTimeToTimeZone({ dateTime: startDate, format: 'PPP', timeZone })} ${slot}`}
          </Text>
        </div>
        {canBeRescheduled && (
          <div
            data-pointer="reschedule-inspection"
            className="mb-xl flex w-full justify-end sm:mt-sm"
          >
            <Button onClick={handleRescheduleClick} variant="text">
              {SECTIONS_STRINGS['upcoming_appointments.reschedule']}
            </Button>
          </div>
        )}
      </div>
      <RescheduleAppointmentModal
        coverageRegionId={coverageRegionId}
        isOpen={isRescheduleAppointmentModalOpen}
        onDismiss={handleRescheduleAppointmentModalDismiss}
        timeZone={timeZone}
        unit={unit}
        unitId={unitId}
        isHandledByAnExternalProvider={isHandledByAnExternalProvider}
      />
    </>
  );
}
